import { GetUserProjectsQuery } from '@pypestream/api-services';

type RawProject =
  | GetUserProjectsQuery['tenancy_users'][number]['user_accounts'][number]['user_account_projects'][number]['project']
  | GetUserProjectsQuery['tenancy_users'][number]['_user_teams'][number]['team']['team_accounts'][number]['team_account_projects'][number]['project'];

export type ProjectTool = {
  id: string;
  name: string;
  displayName?: string | null;
};

export type UserProject = RawProject & {
  role: string;
  availableTools: ProjectTool[];
};

type RoleEntry =
  GetUserProjectsQuery['tenancy_users'][number]['user_accounts'][number]['user_account_projects'][number]['_user_account_project_roles'][number];
type ProductSetting =
  GetUserProjectsQuery['tenancy_users'][number]['user_accounts'][number]['user_account_projects'][number]['project']['project_product_settings'][number];

const getProStudioRoleName = (roles: RoleEntry[]): string | undefined =>
  roles.find((r) => r.role.product.name === 'PRO_STUDIO')?.role.name;

// Builds available tools from roles (source of truth for access) enriched with
// displayName from project_product_settings (Manager/ORGANIZATION is not in settings).
const getAvailableTools = (
  roles: RoleEntry[],
  productSettings: ProductSetting[] | null | undefined
): ProjectTool[] => {
  const displayNames = new Map(
    (productSettings ?? []).map((s) => [s.product.id, s.product.displayName])
  );

  const toolsMap = new Map<string, ProjectTool>();
  roles.forEach(({ role }) => {
    if (!toolsMap.has(role.product.id)) {
      toolsMap.set(role.product.id, {
        id: role.product.id,
        name: role.product.name,
        displayName: displayNames.get(role.product.id) ?? null,
      });
    }
  });

  return Array.from(toolsMap.values());
};

export const extractUserProjects = (
  data?: GetUserProjectsQuery['tenancy_users'][number]
): UserProject[] => {
  const projectMap = new Map<string, UserProject>();

  data?.user_accounts.forEach((userAccount) => {
    userAccount.user_account_projects.forEach((userAccountProject) => {
      const role = getProStudioRoleName(
        userAccountProject._user_account_project_roles
      );
      if (userAccountProject.project && role) {
        projectMap.set(userAccountProject.project.id, {
          ...userAccountProject.project,
          role,
          availableTools: getAvailableTools(
            userAccountProject._user_account_project_roles,
            userAccountProject.project.project_product_settings
          ),
        });
      }
    });
  });

  data?._user_teams.forEach((userTeam) => {
    userTeam.team.team_accounts.forEach((teamAccount) => {
      teamAccount.team_account_projects.forEach((teamAccountProject) => {
        const role = getProStudioRoleName(
          teamAccountProject._team_account_project_roles
        );
        if (
          teamAccountProject.project &&
          role &&
          !projectMap.has(teamAccountProject.project.id)
        ) {
          projectMap.set(teamAccountProject.project.id, {
            ...teamAccountProject.project,
            role,
            availableTools: getAvailableTools(
              teamAccountProject._team_account_project_roles,
              teamAccountProject.project.project_product_settings
            ),
          });
        }
      });
    });
  });

  return Array.from(projectMap.values());
};
